import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'es', label: 'Español', short: 'ES' },
  { code: 'pt', label: 'Português', short: 'PT' },
  { code: 'fr', label: 'Français', short: 'FR' },
];

function LanguageSwitcher({ dark = false }: { dark?: boolean }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = LANGUAGES.find(l => l.code === i18n.language?.split('-')[0]) || LANGUAGES[0];

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    localStorage.setItem('phoenix_lang', code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-full border transition-all duration-300 cursor-pointer whitespace-nowrap text-xs font-semibold uppercase tracking-wider ${
          open
            ? 'border-[#C9A84C] text-[#C9A84C] bg-[#C9A84C]/10'
            : dark
            ? 'border-white/20 text-white/70 hover:border-[#C9A84C] hover:text-[#C9A84C]'
            : 'border-white/20 text-white/70 hover:border-[#C9A84C] hover:text-[#C9A84C]'
        }`}
        aria-label="Change language"
      >
        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-global-line text-sm" /></span>
        {current.short}
        <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-40 bg-[#1e1e1e] border border-white/10 rounded-xl overflow-hidden z-50">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors duration-150 cursor-pointer whitespace-nowrap ${
                current.code === lang.code
                  ? 'bg-[#C9A84C]/15 text-[#C9A84C] font-semibold'
                  : 'text-white/70 hover:bg-white/5 hover:text-white'
              }`}
            >
              <span>{lang.label}</span>
              {current.code === lang.code && <i className="ri-check-line text-[#C9A84C] text-sm" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const NAV_LINKS = [
    { labelKey: 'nav_home', href: '#home', isRoute: false },
    { labelKey: 'nav_about', href: '#about', isRoute: false },
    { labelKey: 'nav_services', href: '#services', isRoute: false },
    { labelKey: 'nav_fleet', href: '/fleet', isRoute: true },
    { labelKey: 'nav_schedules', href: '/schedules', isRoute: true },
    { labelKey: 'nav_amenities', href: '/amenities', isRoute: true },
    { labelKey: 'nav_contact', href: '#contact', isRoute: false },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string, isRoute: boolean) => {
    setMobileOpen(false);
    if (isRoute) {
      window.REACT_APP_NAVIGATE?.(href);
    } else if (!isHomePage) {
      window.REACT_APP_NAVIGATE?.('/');
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-[#1a1a1a] shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-32">
          {/* Logo */}
          <a
            href="#home"
            onClick={(e) => { e.preventDefault(); handleNavClick('#home', false); }}
            className="flex items-center gap-3 cursor-pointer"
          >
            <img
              src="https://storage.readdy-site.link/project_files/543a30f0-c088-40ca-b55f-d366a28be6b3/d8bdbfe7-b9ea-40a1-a426-22f1e1a4a677_12.png?v=1abd8e4dc9705f9939d4c05b25cb73fa"
              alt="Phoenix Transportation Logo"
              className="h-28 w-auto object-contain"
            />
          </a>

          {/* Desktop Links */}
          <ul className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); handleNavClick(link.href, link.isRoute); }}
                  className="text-white/90 hover:text-[#C9A84C] text-sm font-medium tracking-widest uppercase transition-colors duration-300 cursor-pointer whitespace-nowrap"
                >
                  {t(link.labelKey)}
                </a>
              </li>
            ))}
          </ul>

          {/* Right: Language + Book Now */}
          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher />
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); handleNavClick('#contact', false); }}
              className="inline-flex items-center gap-2 bg-[#C9A84C] hover:bg-[#b8953e] text-white text-sm font-semibold tracking-wider uppercase px-6 py-3 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t('nav_bookNow')}
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="lg:hidden flex flex-col gap-1.5 cursor-pointer p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${mobileOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 bg-[#1a1a1a]/98 flex flex-col items-center justify-center transition-all duration-500 lg:hidden ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <ul className="flex flex-col items-center gap-7">
          {NAV_LINKS.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(link.href, link.isRoute); }}
                className="text-white hover:text-[#C9A84C] text-2xl font-medium tracking-widest uppercase transition-colors duration-300 cursor-pointer"
              >
                {t(link.labelKey)}
              </a>
            </li>
          ))}
          <li className="pt-2">
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); handleNavClick('#contact', false); }}
              className="bg-[#C9A84C] text-white text-lg font-semibold tracking-wider uppercase px-8 py-3 rounded-full cursor-pointer whitespace-nowrap"
            >
              {t('nav_bookNow')}
            </a>
          </li>
          <li className="pt-2">
            <LanguageSwitcher />
          </li>
        </ul>
      </div>
    </>
  );
}
